import SlotPageWrapper from '@/components/SlotPageWrapper'
import { WorkOSUser } from '@/components/UserProfileSettingDetail'
import { Stack, Text } from '@/components/custom-components'
import { Loader } from '@/components/custom-components/Loader'
import { Button } from '@/components/shadcn/button'
import useLogout from '@/hooks/useLogout'
import useMonitorSubscription from '@/hooks/useMonitorSubscription'
import showToastNotification from '@/hooks/useShowToast'
import { useTeamByInvitation } from '@/hooks/useTeamByInvitations'
import { trpc } from '@/utils/trpc'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

type SearchParams = {
  token?: string
}

export const Route = createFileRoute('/invite')({
  component: Invite,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return search
  },
})

export default function Invite() {
  const navigate = useNavigate()

  const { token } = Route.useSearch()
  const [hasResponded, setHasResponded] = useState(false)
  const [user, set_user] = useLocalStorage<WorkOSUser | undefined>('_wos_user', undefined)
  const { logout } = useLogout()
  const [responseStatus, setResponseStatus] = useState<'accepted' | 'rejected' | null>(null)
  const [responseSelected, setResponseSelected] = useState<'accepted' | 'rejected' | null>(null)
  const { subscriptionSuccessful, isLoading: isMonitoringSubscription } = useMonitorSubscription(
    hasResponded && responseStatus == 'accepted',
  )

  const { mutate: switchAccount, isPending: isSwitchingAccount } = trpc.switchAccount.useMutation({
    onSuccess: async (result) => {
      if (user && result.mode && result.teamId) {
        set_user({
          ...user,
          mode: result.mode,
          teamId: result.teamId,
        })
      }
      showToastNotification('success', {
        message: `Success`,
        description: `Switched account successfully.`,
      })
      setTimeout(() => {
        navigate({ to: '/feeds/templates', search: { welcomePage: true } })
      }, 1500)
    },
    onError: (error) => {
      showToastNotification('error', {
        message: `Error switching account.`,
        description: `${error.message}`,
      })
    },
  })
  const { teamData, isLoading, isRefetching, isQueryError, refetch } = useTeamByInvitation(token)

  const {
    mutateAsync: changeInvitationStatus,
    isPending: isMutating,
    error: mutateError,
  } = trpc.changeInvitationStatus.useMutation({
    onSuccess: (_data, variables) => {
      setHasResponded(true)
      setResponseStatus(variables.status)
      setResponseSelected(null)
      if (variables.status !== 'accepted') {
        setTimeout(() => {
          navigate({ to: '/feeds/templates', search: { welcomePage: false } })
        }, 1000)
      }
    },
    onError: () => {
      setResponseSelected(null)
    },
  })

  const getInviteLabel = (hasResponded: boolean, responseStatus: 'accepted' | 'rejected' | null) => {
    if (!hasResponded) {
      return 'Accept Invite'
    }
    return responseStatus === 'accepted' ? 'Invite Accepted' : 'Invite Declined'
  }

  useEffect(() => {
    if (token) refetch()
  }, [token, refetch])

  useEffect(() => {
    if (subscriptionSuccessful) {
      switchAccount({ teamId: teamData?.id })
    }
  }, [subscriptionSuccessful])

  // Handle invalid or expired invites
  if (!token || isQueryError) {
    return (
      <SlotPageWrapper>
        <div className="flex flex-col items-center bg-themebackground  gap-5 rounded-xl min-w-96">
          <Text size="xl" weight="semibold" className="text-center">
            Invalid Invite
          </Text>
          <Text size="sm" className="text-center">
            The invite link is invalid or has expired.
          </Text>
          <Stack className="gap-2 w-full">
            <Link to="/" className="w-full">
              <Button className="w-full">Leave Page</Button>
            </Link>
          </Stack>
        </div>
      </SlotPageWrapper>
    )
  }

  return (
    <SlotPageWrapper>
      <Text size="xl" weight="semibold" className="text-center">
        {getInviteLabel(hasResponded, responseStatus)}
      </Text>

      {isLoading || isRefetching ? (
        <Loader />
      ) : hasResponded ? (
        <Stack className="gap-3 items-center">
          <Text size="sm" className="text-center">
            {responseStatus === 'accepted'
              ? 'You have successfully joined the team.'
              : 'You have declined the invitation.'}
          </Text>
          <Loader />
        </Stack>
      ) : (
        <>
          <img
            src={teamData?.avatar || undefined}
            alt={teamData?.name ? `${teamData.name} avatar` : 'Team avatar'}
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).onerror = null
              e.currentTarget.src = '/default-team-avatar.svg'
            }}
            className="w-24 h-24 min-w-24 rounded-full bg-slate-200"
          />
          <Text size="xl" weight="semibold" className="text-center">
            {teamData?.name}
          </Text>
          <Stack className="gap-2 w-full">
            <Button
              className="w-full"
              onClick={() => {
                setResponseSelected('accepted')
                changeInvitationStatus({ status: 'accepted', token })
              }}
              disabled={
                isMutating ||
                isSwitchingAccount ||
                (isMonitoringSubscription && hasResponded && responseStatus == 'accepted')
              }
              loading={(isMutating || isSwitchingAccount || isMonitoringSubscription) && responseSelected == 'accepted'}
            >
              Accept Invite
            </Button>
            <Button
              className="w-full"
              variant="secondary"
              onClick={() => {
                setResponseSelected('rejected')
                changeInvitationStatus({ status: 'rejected', token })
              }}
              disabled={
                isMutating ||
                isSwitchingAccount ||
                (isMonitoringSubscription && hasResponded && responseStatus == 'accepted')
              }
              loading={(isMutating || isSwitchingAccount || isMonitoringSubscription) && responseSelected == 'rejected'}
            >
              Decline Invite
            </Button>
          </Stack>
          {mutateError ? (
            <Text size="sm" className="text-center text-red-600">
              {mutateError?.message || 'Could not update invitation. '}
            </Text>
          ) : null}
          <span className="text-center">
            <span className="text-thememutedforeground">Can’t decide?</span>{' '}
            <span
              className="underline font-medium cursor-pointer"
              onClick={async () => {
                logout(token ? false : true)
                if (token) navigate({ to: '/feeds/templates' })
              }}
            >
              Skip for now
            </span>
            , <span className="mx-1 text-thememutedforeground">or</span>{' '}
            <span
              className="underline font-medium cursor-pointer"
              onClick={async () => {
                logout(token ? false : true)
                if (token) navigate({ to: '/login', search: { redirect: `/invite?token=${token}` } })
              }}
            >
              log in with a different account
            </span>
          </span>
        </>
      )}
    </SlotPageWrapper>
  )
}
